import { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { SESSION_TIMEOUT_MS } from '@/constants'
import { useAppDispatch, showSnackbar } from '@/store'
import cookieService from '@/utils/cookies'
import axiosInstance, { clearTokens, updateLastActivity } from '@/lib/axios/axiosInstance'
import { apiPath } from '@/constants/api-paths'

/**
 * useSessionTimeout — hard logout after 30 min of inactivity.
 * Inactivity = no mouse, keyboard, scroll, touch, or API calls.
 */
export const useSessionTimeout = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isLoggedIn = !!cookieService.getValue('access_token')
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const handleLogout = useCallback(async () => {
    console.warn('[SESSION] Inactivity timeout reached — logging out user')
    try {
      await axiosInstance.post(apiPath.LOGOUT_USER)
    } catch {
      console.warn('[SESSION] Logout API failed — clearing tokens locally anyway')
    }

    clearTokens()
    // Drop the React Query cache so the next user's session doesn't see
    // the previous user's profile / list data on first paint.
    queryClient.clear()
    navigate('/auth/login', { replace: true })
    dispatch(
      showSnackbar({
        severity: 'warning',
        message: 'Session expired due to inactivity. Please log in again.',
      }),
    )
  }, [dispatch, navigate, queryClient])

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    updateLastActivity()
    timerRef.current = setTimeout(handleLogout, SESSION_TIMEOUT_MS)
  }, [handleLogout])

  useEffect(() => {
    if (!isLoggedIn) return

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'] as const
    events.forEach((e) => window.addEventListener(e, resetTimer))
    window.addEventListener('api-activity', resetTimer) // axios request interceptor dispatches this

    resetTimer() // start timer on mount

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer))
      window.removeEventListener('api-activity', resetTimer)
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [isLoggedIn, resetTimer])
}
